// Group dashboard - /Home/Dashboard. Design: dashboard-multi-branch.html.
//
// One sales-report and one product-wise-sale call per branch the user can see
// (the header's store list), for the period, its comparison and the 12-day
// sparkline window. Blocks the server cannot answer yet keep their place and
// say "needs backend" - see docs/BACKEND-COMPLETE.md (Part 8).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setBranch, setBranchId, setBranchName } from "../../redux/branchlist";
import { fetchAMCstatus } from "../../redux/amcstatus";
import { all_routes } from "../../Router/all_routes";
import {
  Card,
  DashScreen,
  DataTable,
  Delta,
  Drawer,
  Hero,
  LineTrend,
  MicroSpark,
  MiniBtn,
  PERIODS,
  PeriodBar,
  Pending,
  Queue,
  Seg,
  Sparkline,
  StackBar,
  Tile,
  Todo,
  cap,
  cmpRange,
  compact,
  dailyTotals,
  dayLabel,
  downloadCsv,
  fetchInvoiceSpans,
  fetchItemSales,
  fmtPct,
  hourLbl,
  hourWindow,
  hourlyTotals,
  inRange,
  longDate,
  money,
  moneyDp,
  paceCut,
  pct,
  rangeFor,
  sparkRange,
  sumOf,
  useDashKeys,
  useToast,
  useUpdatedLabel,
  valueOf,
} from "../../components/dashboard/ui";

// colour follows the branch (its place in the store list), never its rank
const colourOf = (i) => (i < 6 ? `var(--s${i + 1})` : `hsl(${(i * 67) % 360} 55% 50%)`);

const RANKS = [
  { id: "sales", label: "Sales" },
  { id: "gpPct", label: "Margin" },
  { id: "tgt", label: "Target" },
  { id: "growth", label: "Growth" },
];
const LCOLS = [
  { k: "rank", t: "#", w: 46 },
  { k: "name", t: "Branch" },
  { k: "sales", t: "Net sales", num: true },
  { k: "growth", t: "vs prev", num: true },
  { k: "gpPct", t: "Margin", num: true, todo: true },
  { k: "inv", t: "Invoices", num: true },
  { k: "avg", t: "Avg bill", num: true },
  { k: "coll", t: "Collected", num: true, todo: true },
  { k: "ar", t: "Receivable", num: true, todo: true },
  { k: "stock", t: "Stock value", num: true, todo: true },
  { k: "tgt", t: "Target", num: true, todo: true },
  { k: "trend", t: "12-day trend", num: true },
];
const SORTABLE = ["sales", "growth", "inv", "avg"];

const GroupDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { BranchByUser } = useSelector((state) => state.userBranch);

  const branches = useMemo(
    () =>
      (Array.isArray(BranchByUser) ? BranchByUser : [])
        .map((o) => o?.branch)
        .filter((b) => b && b.guid)
        .map((b, i) => ({ b, n: b.name || "Branch", c: colourOf(i) })),
    [BranchByUser]
  );
  const branchKey = branches.map((x) => x.b.guid).join("|");

  const [period, setPeriod] = useState("today");
  const [cmp, setCmp] = useState("prev");
  const [vatOn, setVatOn] = useState(true);
  const [rank, setRank] = useState("sales");
  const [off, setOff] = useState({}); // branch guid -> left out of scope
  const [hidden, setHidden] = useState({}); // branch name -> hidden on the chart
  const [tables, setTables] = useState({});
  const [drawer, setDrawer] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stamp, setStamp] = useState(null);
  const [reload, setReload] = useState(0);
  const [toast, showToast] = useToast();
  const updated = useUpdatedLabel(stamp);
  const req = useRef(0);

  /* ---------------- load ---------------- */
  useEffect(() => {
    if (!branches.length) return;
    const id = ++req.current;
    const r = rangeFor(period);
    const c = cmpRange(r, cmp);
    const sp = sparkRange(r);
    setLoading(true);
    setError("");
    Promise.all(
      branches.map((x) =>
        Promise.all([fetchInvoiceSpans(x.b, [r, c, sp]), fetchItemSales(x.b, r.from, r.to).catch(() => [])])
          .then(([inv, items]) => ({ guid: x.b.guid, inv, items }))
          .catch((e) => {
            console.error("Dashboard load failed for", x.n, e?.response?.data?.Message || e);
            return { guid: x.b.guid, inv: [], items: [], failed: true };
          })
      )
    )
      .then((list) => {
        if (id !== req.current) return;
        const failed = branches.filter((x) => list.find((l) => l.guid === x.b.guid)?.failed).map((x) => x.n);
        if (failed.length) setError(`Could not load ${failed.join(", ")}. Their figures show as zero - press Refresh to retry.`);
        setData({ r, c, sp, by: Object.fromEntries(list.map((l) => [l.guid, l])) });
        setStamp(Date.now());
      })
      .finally(() => id === req.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey, period, cmp, reload]);

  const visible = branches.filter((x) => !off[x.b.guid]);

  /* ---------------- derived ---------------- */
  const v = useMemo(() => {
    if (!data) return null;
    const { r, c, sp, by } = data;
    const f = valueOf(vatOn);
    const rows = visible.map((x) => {
      const all = by[x.b.guid]?.inv || [];
      const cur = inRange(all, r.from, r.to);
      const prev = paceCut(inRange(all, c.from, c.to), c.to, r.live);
      const sales = sumOf(cur, f);
      const prevSales = sumOf(prev, f);
      return {
        ...x,
        all,
        cur,
        prev,
        items: by[x.b.guid]?.items || [],
        sales,
        prevSales,
        growth: prevSales ? ((sales - prevSales) / prevSales) * 100 : 0,
        inv: cur.length,
        pinv: prev.length,
        avg: cur.length ? sales / cur.length : 0,
        spark: dailyTotals(all, sp.from, sp.to, f).map((d) => d.v),
      };
    });
    const total = sumOf(rows, (x) => x.sales);
    rows.forEach((x) => (x.share = pct(x.sales, total)));
    const sparkGroup = rows.length ? rows[0].spark.map((_, i) => sumOf(rows, (x) => x.spark[i])) : [];
    const sparkInv = rows.length
      ? dailyTotals([], sp.from, sp.to, () => 0).map((d) => sumOf(rows, (x) => x.all.filter((i) => i.date === d.date).length))
      : [];
    return {
      r,
      c,
      f,
      rows,
      total,
      prevTotal: sumOf(rows, (x) => x.prevSales),
      inv: sumOf(rows, (x) => x.inv),
      pinv: sumOf(rows, (x) => x.pinv),
      sparkGroup,
      sparkInv,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, vatOn, off, branchKey]);

  const league = useMemo(() => {
    if (!v) return [];
    const k = SORTABLE.includes(rank) ? rank : "sales";
    const out = v.rows.slice().sort((a, b) => b[k] - a[k]);
    out.forEach((x, i) => (x.rank = i + 1));
    return out;
  }, [v, rank]);

  /* ---------------- trend ---------------- */
  const trend = useMemo(() => {
    if (!v || !v.rows.length) return null;
    const { r, f, rows } = v;
    if (r.n === 1) {
      const win = hourWindow(rows.map((x) => x.cur), r.live);
      if (win)
        return {
          unit: "hour",
          x: hourlyTotals([], win, f).map((o) => hourLbl(o.h)),
          series: rows.map((x) => ({ n: x.n, c: x.c, v: hourlyTotals(x.cur, win, f).map((o) => o.v) })),
        };
      const sp = sparkRange(r);
      return {
        unit: "day",
        noTimes: true,
        x: dailyTotals([], sp.from, sp.to, f).map((o) => dayLabel(o.date)),
        series: rows.map((x) => ({ n: x.n, c: x.c, v: x.spark })),
      };
    }
    return {
      unit: "day",
      x: dailyTotals([], r.from, r.to, f).map((o) => dayLabel(o.date)),
      series: rows.map((x) => ({ n: x.n, c: x.c, v: dailyTotals(x.cur, r.from, r.to, f).map((o) => o.v) })),
    };
  }, [v]);

  /* ---------------- top items across the group ---------------- */
  const topItems = useMemo(() => {
    if (!v) return [];
    const m = {};
    v.rows.forEach((x) =>
      x.items.forEach((it) => {
        if (!m[it.name]) m[it.name] = { name: it.name, value: 0, qty: 0, by: [] };
        m[it.name].value += it.value;
        m[it.name].qty += it.qty;
        m[it.name].by.push({ n: x.n, value: it.value, qty: it.qty });
      })
    );
    return Object.values(m)
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [v]);

  /* ---------------- drawer ---------------- */
  const drawerCsv = (d) => {
    downloadCsv(
      `${d.title}.csv`.replace(/[\\/:*?"<>|]/g, "-"),
      d.cols.map((c) => c.t),
      d.rows.map((row) => d.cols.map((c) => (typeof row[c.k] === "object" ? "" : row[c.k])))
    );
    showToast("List exported");
  };
  const openBranch = useCallback(
    (x) => {
      if (!v) return;
      const f = v.f;
      const pair = (m, a, b) => ({ m, v: a, p: b });
      const credit = (rows) => sumOf(rows.filter((i) => i.payType === 4), f);
      setDrawer({
        guid: x.b.guid,
        title: x.n,
        sub: `${cap(v.r.name)} vs ${v.c.name}`,
        cols: [
          { k: "m", t: "Measure" },
          { k: "v", t: cap(v.r.name), num: true },
          { k: "p", t: "Comparison", num: true },
        ],
        rows: [
          pair("Net sales", moneyDp(x.sales), moneyDp(x.prevSales)),
          pair("Share of group", fmtPct(x.share), "—"),
          pair("Invoices", money(x.inv), money(x.pinv)),
          pair("Average bill", moneyDp(x.avg), moneyDp(x.pinv ? x.prevSales / x.pinv : 0)),
          pair("Discount given", moneyDp(sumOf(x.cur, (i) => i.discount)), moneyDp(sumOf(x.prev, (i) => i.discount))),
          pair("VAT", moneyDp(sumOf(x.cur, (i) => i.vat)), moneyDp(sumOf(x.prev, (i) => i.vat))),
          pair("Cash", moneyDp(sumOf(x.cur, (i) => i.cash)), moneyDp(sumOf(x.prev, (i) => i.cash))),
          pair("Card", moneyDp(sumOf(x.cur, (i) => i.card)), moneyDp(sumOf(x.prev, (i) => i.card))),
          pair("Credit sales", moneyDp(credit(x.cur)), moneyDp(credit(x.prev))),
          ...["Gross profit", "Collections", "Receivable (now)", "Stock value (now)", "Target attainment"].map((m) =>
            pair(m, <Todo key={m} />, "—")
          ),
        ],
      });
    },
    [v]
  );
  const openInGroupRows = (title, rows) =>
    setDrawer({
      title,
      sub: `${rows.length} rows · ${visible.length} branches`,
      cols: [
        { k: "br", t: "Branch" },
        { k: "bill", t: "Bill no" },
        { k: "d", t: "Date" },
        { k: "amt", t: "Net total", num: true },
      ],
      rows: rows.map((i) => ({ br: i.br, bill: i.billNo || "—", d: dayLabel(i.date), amt: moneyDp(i.net) })),
    });
  const goBranch = (guid) => {
    const x = branches.find((b) => b.b.guid === guid);
    if (!x) return;
    dispatch(fetchAMCstatus(x.b));
    dispatch(setBranchId({ guid: x.b.guid }));
    dispatch(setBranchName({ name: x.n }));
    dispatch(setBranch(x.b));
    setDrawer(null);
    navigate(all_routes.branchdashboard);
  };

  /* ---------------- handlers ---------------- */
  const refresh = () => {
    setReload((x) => x + 1);
    showToast("Figures refreshed");
  };
  const toggleTable = (k) => setTables((t) => ({ ...t, [k]: !t[k] }));
  const toggleBranch = (guid) => {
    if (!off[guid] && visible.length === 1) return showToast("Keep at least one branch in view");
    setOff((o) => ({ ...o, [guid]: !o[guid] }));
  };
  const allBranches = () => setOff({});
  const toggleSeries = (n) => {
    const shownNow = (trend?.series || []).filter((s) => !hidden[s.n]).length;
    if (!hidden[n] && shownNow === 1) return showToast("Keep at least one branch on the chart");
    setHidden((h) => ({ ...h, [n]: !h[n] }));
  };
  const exportCsv = () => {
    downloadCsv(
      `axoone-group-${period}.csv`,
      ["Rank", "Branch", "Net sales", "Growth %", "Invoices", "Avg bill", "Share %"],
      league.map((x) => [x.rank, x.n, x.sales.toFixed(2), x.growth.toFixed(2), x.inv, x.avg.toFixed(2), x.share.toFixed(2)])
    );
    showToast("Branch league exported");
  };

  useDashKeys({ onPeriod: setPeriod, onRefresh: refresh, onEscape: () => setDrawer(null), more: { a: allBranches } });

  /* ---------------- render ---------------- */
  const r = v?.r || rangeFor(period);
  const c = v?.c || cmpRange(r, cmp);
  const all = visible.length === branches.length;
  const title = !branches.length
    ? "All branches"
    : all
      ? "All branches"
      : visible.length <= 3
        ? visible.map((x) => x.n).join(" · ")
        : `${visible.length} of ${branches.length} branches`;
  const sub = !branches.length
    ? "No branches are assigned to this user"
    : period === "today"
      ? `${longDate(r.to)} · consolidated across ${visible.length} branches`
      : `${cap(r.name)} · ${r.n === 1 ? dayLabel(r.to) : dayLabel(r.from) + " → " + dayLabel(r.to)} · ${visible.length} branches`;

  const best = league[0];
  const maxShare = league.reduce((m, x) => Math.max(m, x.share), 1);
  const shown = (trend?.series || []).filter((s) => !hidden[s.n]);
  const shareRows = v ? v.rows.slice().sort((a, b) => b.sales - a.sales) : [];

  // needs a decision
  const idle = v ? v.rows.filter((x) => !x.inv) : [];
  const falling = v ? v.rows.filter((x) => x.prevSales && x.growth <= -10) : [];
  const pendingBills = v ? v.rows.flatMap((x) => x.cur.filter((i) => i.status === 1).map((i) => ({ ...i, br: x.n }))) : [];
  const queue = [
    {
      key: "idle",
      sev: idle.length ? "crit" : "info",
      ic: "chart",
      t: idle.length ? `${idle.length} ${idle.length === 1 ? "branch has" : "branches have"} no sales ${r.name}` : `Every branch is trading ${r.name}`,
      s: idle.length ? idle.map((x) => x.n).join(", ") : "nothing to chase",
      n: idle.length ? `${idle.length} of ${visible.length}` : "All clear",
    },
    {
      key: "falling",
      sev: falling.length ? "warnx" : "info",
      ic: "chart",
      t: falling.length ? "Branches down 10% or more" : "No branch is down 10% or more",
      s: falling.length ? falling.map((x) => `${x.n} ${fmtPct(x.growth, 0)}`).join(", ") : `vs ${c.name}`,
      n: falling.length ? `${falling.length} of ${visible.length}` : "All clear",
    },
    {
      key: "pending",
      sev: pendingBills.length ? "warnx" : "info",
      ic: "doc",
      t: pendingBills.length ? "Bills still pending, group-wide" : "No pending bills",
      s: "raised but not completed",
      n: pendingBills.length ? `${pendingBills.length} bills` : "All clear",
    },
    { key: "target", sev: "crit", ic: "chart", t: "Branches under 85% of target", s: "branch targets", todo: true },
    { key: "ar90", sev: "crit", ic: "clock", t: "Receivables past 90 days, group-wide", s: "customer ageing", todo: true },
    { key: "transfer", sev: "warnx", ic: "truck", t: "Stock transfers pending acceptance", s: "stock is in limbo until confirmed", todo: true },
    { key: "price", sev: "warnx", ic: "tag", t: "Same item priced differently across branches", s: "check before the next promotion", todo: true },
    { key: "dayclose", sev: "warnx", ic: "lock", t: "Branches that have not closed the day", s: "cash and stock stay unreconciled", todo: true },
    { key: "misplaced", sev: "warnx", ic: "box", t: "Stock sitting in the wrong branch", s: "out of stock here, dead stock there", todo: true },
    { key: "credit", sev: "info", ic: "card", t: "Customers over their credit limit", s: "group-wide exposure", todo: true },
    { key: "wast", sev: "info", ic: "box", t: "Damage & wastage above the group average", s: "per-branch wastage", todo: true },
  ];
  const onQueue = (x) => {
    if (x.key === "pending") openInGroupRows("Bills still pending", pendingBills);
    else if (x.key === "idle" && idle.length) showToast(`No invoices from ${idle.map((b) => b.n).join(", ")}`);
    else if (x.key === "falling" && falling.length) openBranch(falling[0]);
  };

  const drawerEl = (
    <Drawer
      data={drawer}
      onClose={() => setDrawer(null)}
      onCsv={drawerCsv}
      primary={
        drawer?.guid ? (
          <button type="button" className="axd-btn primary" onClick={() => goBranch(drawer.guid)}>
            Open branch dashboard
          </button>
        ) : (
          <button type="button" className="axd-btn primary" onClick={() => setDrawer(null)}>
            Done
          </button>
        )
      }
    />
  );

  return (
    <DashScreen
      title={title}
      sub={sub}
      updated={updated}
      loading={loading}
      error={error}
      drawer={drawerEl}
      toast={toast}
      keys={[
        ["T", "Today"],
        ["W", "Week"],
        ["M", "Month"],
        ["A", "All branches"],
        ["R", "Refresh"],
        ["Esc", "Close"],
      ]}
      status={`${visible.length} of ${branches.length} branches · ${vatOn ? "incl. VAT" : "excl. VAT"} · ${r.n} ${r.n === 1 ? "day" : "days"}`}
    >
      <PeriodBar
        periods={PERIODS}
        period={period}
        onPeriod={setPeriod}
        cmp={cmp}
        onCmp={setCmp}
        vat={vatOn}
        onVat={setVatOn}
        vatLabel="With VAT"
        onRefresh={refresh}
        onCsv={exportCsv}
        onPrint={() => window.print()}
      />

      <div className="axd-scope">
        <span className="lb">Branches</span>
        {branches.map((x) => (
          <button
            type="button"
            key={x.b.guid}
            className={"axd-bchip" + (off[x.b.guid] ? "" : " on")}
            onClick={() => toggleBranch(x.b.guid)}
          >
            <span className="sw" style={{ background: x.c }} />
            {x.n}
          </button>
        ))}
        <button type="button" className="axd-btn" onClick={allBranches}>
          All
        </button>
      </div>

      {/* ============ KPI ROW ============ */}
      <section className="axd-kpirow">
        <Hero
          label={`${all ? "Group net sales" : `${visible.length} branches`} · ${r.name}`}
          value={money(v?.total || 0)}
          loading={!v && loading}
          prev={v ? { cur: v.total, prev: v.prevTotal } : undefined}
          cmpName={c.name}
          spark={<Sparkline vals={v?.sparkGroup || []} w={256} h={40} />}
          footLabel={r.live ? "Target to now" : "Group target"}
          foot={<b>—</b>}
        />
        <div className="axd-tiles">
          <Tile label="Gross profit" todo needs="needs item cost on the sales report" />
          <Tile
            label="Invoices"
            value={money(v?.inv || 0)}
            delta={v ? <Delta cur={v.inv} prev={v.pinv} small /> : null}
            sub={`avg bill ${money(v && v.inv ? v.total / v.inv : 0)}`}
            spark={<Sparkline vals={v?.sparkInv || []} />}
            onClick={() =>
              v &&
              openInGroupRows(
                `Invoices · ${r.name}`,
                v.rows.flatMap((x) => x.cur.map((i) => ({ ...i, br: x.n })))
              )
            }
          />
          <Tile label="Collections" todo needs="needs a receipts summary" />
          <Tile label="Purchases" todo needs="needs a purchases summary" />
          <Tile label="Receivables" todo needs="needs customer balances" />
          <Tile label="Branches on target" todo needs={best ? `best ${r.name}: ${best.n}` : "needs branch targets"} />
        </div>
      </section>

      <section className="axd-g12">
        {/* LEAGUE TABLE */}
        <Card
          className="c12"
          title="Branch league"
          note={v ? `${league.length} branches · ${cap(r.name)} vs ${c.name}` : ""}
          right={
            <>
              <span className="note" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                rank by
              </span>
              <Seg small items={RANKS} value={rank} onChange={setRank} disabledIds={["gpPct", "tgt"]} />
            </>
          }
        >
          <div className="axd-lwrap">
            <table className="axd-lg">
              <thead>
                <tr>
                  {LCOLS.map((col) => (
                    <th key={col.k} className={col.num ? "num" : undefined} style={col.w ? { width: col.w } : undefined}>
                      {SORTABLE.includes(col.k) ? (
                        <button type="button" className="h" onClick={() => setRank(col.k)}>
                          {col.t}
                          {rank === col.k ? <span style={{ color: "var(--accent)" }}>▾</span> : null}
                        </button>
                      ) : (
                        <span className="h">
                          {col.t}
                          {col.todo ? <Todo>api</Todo> : null}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {league.map((x) => (
                  <tr key={x.b.guid} onClick={() => openBranch(x)}>
                    <td>
                      <span className={"axd-rk " + (x.rank === 1 ? "top" : x.rank === league.length && league.length > 2 ? "bot" : "")}>
                        {x.rank}
                      </span>
                    </td>
                    <td>
                      <div className="axd-bcell">
                        <span className="sw" style={{ background: x.c }} />
                        <div>
                          <b>{x.n}</b>
                          <span className="axd-sharebar">
                            <i style={{ width: pct(x.share, maxShare).toFixed(1) + "%", background: x.c }} />
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="num">
                      <b>{money(x.sales)}</b>
                      <small>{fmtPct(x.share)} of group</small>
                    </td>
                    <td className="num">
                      <Delta cur={x.sales} prev={x.prevSales} small />
                    </td>
                    <td className="num na">—</td>
                    <td className="num">{money(x.inv)}</td>
                    <td className="num">{money(x.avg)}</td>
                    <td className="num na">—</td>
                    <td className="num na">—</td>
                    <td className="num na">—</td>
                    <td className="num na">—</td>
                    <td className="num">
                      <MicroSpark vals={x.spark} color={x.c} />
                    </td>
                  </tr>
                ))}
                {!league.length && (
                  <tr>
                    <td colSpan={LCOLS.length} className="na" style={{ textAlign: "center" }}>
                      {loading ? "Loading…" : branches.length ? "No figures yet." : "No branches are assigned to this user."}
                    </td>
                  </tr>
                )}
              </tbody>
              {v && league.length > 0 && (
                <tfoot>
                  <tr>
                    <td />
                    <td>Group total</td>
                    <td className="num">{money(v.total)}</td>
                    <td className="num">
                      <Delta cur={v.total} prev={v.prevTotal} small />
                    </td>
                    <td className="num" />
                    <td className="num">{money(v.inv)}</td>
                    <td className="num">{money(v.inv ? v.total / v.inv : 0)}</td>
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        {/* TREND */}
        <Card
          className="c8"
          title={trend?.unit === "hour" ? "Sales by branch, through the day" : "Sales by branch, day by day"}
          note={
            trend
              ? trend.noTimes
                ? "last 12 days · invoice times not sent"
                : `${shown.length} of ${trend.series.length} branches shown · click a name to hide it`
              : ""
          }
          right={
            <MiniBtn on={tables.trend} onClick={() => toggleTable("trend")}>
              {tables.trend ? "Chart" : "Table"}
            </MiniBtn>
          }
          bodyClass="col"
        >
          {!trend ? (
            <div className="axd-nochart axd-empty">{loading ? "Loading…" : "No figures yet."}</div>
          ) : tables.trend ? (
            <DataTable
              cols={[
                { k: "x", t: trend.unit === "hour" ? "Hour" : "Date" },
                ...trend.series.map((s) => ({ k: s.n, t: s.n, num: true })),
                { k: "__g", t: "Group", num: true },
              ]}
              rows={trend.x.map((s, i) => {
                const row = { x: s, __g: <b>{money(sumOf(trend.series, (se) => se.v[i] || 0))}</b> };
                trend.series.forEach((se) => (row[se.n] = money(se.v[i] || 0)));
                return row;
              })}
            />
          ) : (
            <LineTrend x={trend.x} series={shown} />
          )}
          {trend && (
            <div className="axd-legend chips">
              {trend.series.map((s) => (
                <button type="button" key={s.n} className={"axd-lgbtn" + (hidden[s.n] ? " off" : "")} onClick={() => toggleSeries(s.n)}>
                  <span className="ln" style={{ background: s.c }} />
                  {s.n}
                </button>
              ))}
              {trend.noTimes ? (
                <span className="li">
                  hourly split <Todo />
                </span>
              ) : null}
            </div>
          )}
        </Card>

        {/* CONTRIBUTION */}
        <Card
          className="c4"
          title="Share of group sales"
          right={
            <MiniBtn on={tables.share} onClick={() => toggleTable("share")}>
              {tables.share ? "Chart" : "Table"}
            </MiniBtn>
          }
        >
          {tables.share ? (
            <DataTable
              cols={[
                { k: "n", t: "Branch" },
                { k: "v", t: "Net sales", num: true },
                { k: "p", t: "Share", num: true },
              ]}
              rows={[
                ...shareRows.map((x) => ({ n: x.n, v: moneyDp(x.sales), p: fmtPct(x.share) })),
                { __total: true, n: "Group", v: moneyDp(v?.total || 0), p: "100.0%" },
              ]}
            />
          ) : (
            <>
              <StackBar parts={shareRows.map((x) => ({ n: x.n, v: x.sales, c: x.c }))} />
              <div className="axd-stkey one">
                {shareRows.map((x) => (
                  <span className="k" key={x.b.guid}>
                    <span className="sw" style={{ background: x.c }} />
                    <span className="kn">{x.n}</span>
                    <span className="kv">{money(x.sales)}</span>
                    <span className="kp">{fmtPct(x.share)}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* STOCK TRANSFERS */}
        <Card className="c5" title="Stock moving between branches">
          <Pending
            what="Stock transfers"
            needs="Value moved, notes in transit, notes not yet accepted and the busiest branch-to-branch lanes."
          />
        </Card>

        {/* EXCEPTIONS */}
        <Card
          className="c4"
          title="Needs a decision"
          note={`${queue.filter((x) => !x.todo).length} live · ${queue.filter((x) => x.todo).length} waiting on server`}
        >
          <Queue items={queue} onPick={onQueue} />
        </Card>

        {/* GROUP STOCK */}
        <Card className="c3" title="Group stock">
          <Pending what="Stock position" needs="Stock on hand, cover, dead stock and damage across the group." />
        </Card>

        {/* RECEIVABLES BY BRANCH */}
        <Card className="c6" title="Money owed to us · by branch">
          <Pending compact what="Receivables by age, per branch" needs="Customer balances split into the four age bands below." />
          <div className="axd-legend chips">
            {["Not due / 0–30 days", "31–60 days", "61–90 days", "Over 90 days"].map((n, i) => (
              <span className="axd-lgbtn" key={n}>
                <span className="ln" style={{ height: 10, width: 10, borderRadius: 3, background: `var(--age${i + 1})` }} />
                {n}
              </span>
            ))}
          </div>
        </Card>

        {/* GROUP TOP ITEMS */}
        <Card className="c6" title="Top items across the group" note="and how widely they sell">
          <div className="axd-blist">
            {topItems.length ? (
              topItems.map((x) => {
                const narrow = x.by.length < Math.max(2, Math.ceil(visible.length * 0.6));
                return (
                  <button
                    type="button"
                    className="axd-brow"
                    key={x.name}
                    onClick={() =>
                      setDrawer({
                        title: x.name,
                        sub: `${cap(r.name)} · branch by branch`,
                        cols: [
                          { k: "n", t: "Branch" },
                          { k: "q", t: "Qty", num: true },
                          { k: "v", t: "Value", num: true },
                        ],
                        rows: x.by
                          .slice()
                          .sort((a, b) => b.value - a.value)
                          .map((b) => ({ n: b.n, q: money(b.qty), v: moneyDp(b.value) })),
                      })
                    }
                  >
                    <span>
                      <span className="nm">{x.name}</span>
                      <span className="track">
                        <i style={{ width: pct(x.value, topItems[0].value).toFixed(1) + "%" }} />
                      </span>
                    </span>
                    <span className="v">
                      {compact(x.value)}
                      <small className={narrow ? "warn" : undefined}>
                        sells in {x.by.length}/{visible.length}
                      </small>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="axd-empty">{loading ? "Loading…" : "No item sales in this period."}</div>
            )}
          </div>
        </Card>
      </section>
    </DashScreen>
  );
};

export default GroupDashboard;
